import { AlertCircle, CheckCircle2, Phone, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { api } from "../api.ts";
import { Button, Input } from "./ui/index.ts";

export const PhoneVerification = (
  { onVerified, embedded }: { onVerified: () => void; embedded?: boolean },
) => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<
    { type: "error" | "success" | "info"; text: string } | null
  >(null);
  const [sentCode, setSentCode] = useState(false);

  const handleSendCode = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      if (!phone.trim()) {
        setFeedback({
          type: "error",
          text:
            "Please enter your phone number (e.g. 054-123-4567 or with country code +972...)",
        });
        setLoading(false);
        return;
      }
      await api({
        endpoint: "sendPhoneVerificationCode",
        payload: { phone: phone.trim() },
      });
      setSentCode(true);
      setFeedback({
        type: "success",
        text: "Verification code sent to your phone via SMS",
      });
    } catch (error) {
      console.error("Phone verification send error:", error);
      setFeedback({
        type: "error",
        text: error instanceof Error
          ? error.message
          : "Error sending SMS verification code. Please check your number format.",
      });
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      if (!code.trim()) {
        setFeedback({
          type: "error",
          text: "Please enter the 6-digit verification code from SMS",
        });
        setLoading(false);
        return;
      }
      await api({
        endpoint: "verifyPhoneCode",
        payload: { phone: phone.trim(), code: code.trim() },
      });
      setFeedback({
        type: "success",
        text: "Phone verified successfully!",
      });
      onVerified();
    } catch (error) {
      console.error("Phone verification code check error:", error);
      setFeedback({
        type: "error",
        text: error instanceof Error
          ? error.message
          : "Invalid verification code. Please try again.",
      });
    }
    setLoading(false);
  };

  return (
    <div
      className={embedded
        ? "w-full"
        : "flex items-center justify-center min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black"}
    >
      <div className="w-full max-w-md px-4">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-2xl">
          {!embedded && (
            <>
              <div className="flex justify-center mb-4 text-purple-400">
                <ShieldCheck size={48} />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2 text-center">
                Verify Your Phone
              </h1>
              <p className="text-gray-400 text-center mb-8 text-sm">
                To prevent abuse and protect API access, please verify your
                mobile number via SMS
              </p>
            </>
          )}

          <div className="space-y-4">
            <div>
              <Input
                size="lg"
                type="tel"
                placeholder="054-123-4567 or +972501234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={sentCode}
                icon={<Phone size={20} />}
              />
              {!sentCode && (
                <p className="text-[11px] text-gray-400 mt-1.5 px-1">
                  Enter local Israeli mobile (e.g. 054-123-4567) or
                  international number (+972, +1, +49...)
                </p>
              )}
            </div>

            {sentCode && (
              <div>
                <Input
                  size="lg"
                  type="text"
                  placeholder="Enter 6-digit code from SMS"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <p className="text-[11px] text-gray-400 mt-1.5 px-1">
                  Enter the 6-digit verification code you received via SMS
                </p>
              </div>
            )}

            <Button
              size="lg"
              variant="primary"
              onClick={sentCode ? handleVerifyCode : handleSendCode}
              disabled={!phone || (sentCode && !code)}
              loading={loading}
              className="w-full"
            >
              {sentCode ? "Verify SMS Code" : "Send Verification SMS"}
            </Button>

            {sentCode && (
              <button
                type="button"
                onClick={() => {
                  setSentCode(false);
                  setCode("");
                  setFeedback(null);
                }}
                className="w-full text-xs text-gray-400 hover:text-white transition py-2 text-center"
              >
                Change phone number
              </button>
            )}

            {feedback && (
              <div
                className={`flex items-start gap-2.5 p-3 rounded-xl text-xs ${
                  feedback.type === "error"
                    ? "bg-red-500/10 border border-red-500/20 text-red-300"
                    : feedback.type === "success"
                    ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-300"
                    : "bg-purple-500/10 border border-purple-500/20 text-purple-300"
                }`}
              >
                {feedback.type === "error"
                  ? <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  : <CheckCircle2 size={16} className="shrink-0 mt-0.5" />}
                <span className="leading-relaxed">{feedback.text}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
